import { alchemyConfig } from "@shared/src/config/alchemy";
import { createWalletClient, EIP1193Provider } from "viem";
import { Connector, custom } from "wagmi";
import { AlchemySigner, getSigner as getAlchemySigner } from "@account-kit/core";
import { WalletClientSigner } from "@aa-sdk/core";
import { Chain } from "wagmi/chains";
import { IxSigner } from "@shared/src/stores/useAuthStore";

export interface AuthDetails {
  orgId: string;
  email?: string;
  [key: string]: unknown;
}

export async function poll<T>(callback: () => Promise<T>, waitTime: number, maxAttempts: number) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const result = await callback();
      return result;
    } catch (e) {
      if (i === maxAttempts - 1) {
        throw e;
      }
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
}

export async function pollForAuthDetails(): Promise<AuthDetails> {
  const authDetails = await poll(
    async () => {
      const signer = getEoaOrScaSigner("sca");
      const det = (signer as AlchemySigner)?.getAuthDetails();
      return det;
    },
    1000,
    10,
  );
  if (!authDetails?.orgId) {
    throw new Error("Failed to get organization ID after authentication");
  }
  return authDetails;
}

export function getEoaOrScaSigner(type: "sca" | "eoa", provider?: any) {
  if (provider || type === "eoa") {
    const client = createWalletClient({
      transport: custom(provider!),
    });
    const eoaSigner = new WalletClientSigner(client, "wallet");
    return eoaSigner;
  } else {
    const alchSigner = getAlchemySigner(alchemyConfig);
    return alchSigner;
  }
}

async function initializeEoaSigner(
  connector: Connector,
  chain: Chain,
  lightAccountInitializor: (signer: IxSigner) => Promise<void>,
) {
  const provider = await connector.getProvider();
  const sig = getEoaOrScaSigner("eoa", provider);
  lightAccountInitializor(sig!);
  return sig;
}
